import axios from '../utils/setupAxiosAuth';
import { AuthProvider } from 'react-admin';
import API_ROUTES from '../routes/routes';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { MARKETING_PORTAL_ROLES } from '../roles/portalRoles';
import { UserIdentity, UserRole } from '../types/types';
import { isTokenExpired, isTokenExpiringSoon } from '../utils/jwtUtils';
import { clearAuthStorage } from './authStorage';

const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    clearAuthStorage();
    const response = await axios.post(`${getApiBaseUrl()}${API_ROUTES.login}`, {
      username,
      password,
    });
    const { token, user } = response.data;
    const roleId = Number(user?.role_id || 0);

    if (!MARKETING_PORTAL_ROLES.includes(roleId as UserRole)) {
      clearAuthStorage();
      throw new Error('You do not have access to the Marketing portal');
    }

    localStorage.setItem('user_authToken', token);
    localStorage.setItem('user_id', String(user.id));
    localStorage.setItem('user_name', user.user_name);
    localStorage.setItem('user_fullName', `${user.user_first_name || ''} ${user.user_last_name || ''}`.trim());
    localStorage.setItem('user_role_id', String(roleId));
    localStorage.setItem('user_role', user.role);
    localStorage.setItem('user_username', username);
  },
  logout: () => {
    clearAuthStorage();
    return Promise.resolve();
  },
  checkError: (error) => {
    const status = error.status || error.response?.status;
    // Only 401 clears the session. 403 is a per-resource denial, not a logout.
    if (status === 401) {
      clearAuthStorage();
      return Promise.reject();
    }
    if (status === 403) {
      return Promise.resolve();
    }
    return Promise.resolve();
  },
  checkAuth: () => {
    const token = localStorage.getItem('user_authToken');
    if (!token || isTokenExpired(token)) {
      clearAuthStorage();
      return Promise.reject();
    }
    if (isTokenExpiringSoon(token, 5)) {
      console.log('Marketing token expiring soon');
    }
    return Promise.resolve();
  },
  getPermissions: () => Promise.resolve(localStorage.getItem('user_role')),
  getIdentity: () => {
    const identity: UserIdentity = {
      id: localStorage.getItem('user_id') || '',
      fullName: localStorage.getItem('user_fullName') || '',
      role_id: parseInt(localStorage.getItem('user_role_id') || '0', 10) as UserRole,
      role: localStorage.getItem('user_role') || '',
      avatar: '',
    };
    return Promise.resolve(identity);
  },
};

const getUserRole = (): UserRole | null => {
  const roleId = localStorage.getItem('user_role_id');
  if (!roleId) {
    return null;
  }
  const parsed = parseInt(roleId, 10);
  return Number.isNaN(parsed) ? null : (parsed as UserRole);
};

const isRoleAuthorized = (allowedRoles: UserRole[]): boolean => {
  const userRole = getUserRole();
  if (userRole === null) {
    return false;
  }
  const roleId = Number(userRole);
  return allowedRoles.some((role) => Number(role) === roleId);
};

(authProvider as AuthProvider & {
  getUserRole: typeof getUserRole;
  isRoleAuthorized: typeof isRoleAuthorized;
}).getUserRole = getUserRole;

(authProvider as AuthProvider & {
  getUserRole: typeof getUserRole;
  isRoleAuthorized: typeof isRoleAuthorized;
}).isRoleAuthorized = isRoleAuthorized;

export default authProvider as AuthProvider & {
  getUserRole: typeof getUserRole;
  isRoleAuthorized: typeof isRoleAuthorized;
};
